import { ref, Ref, computed } from 'vue';
import { type LocationQueryRaw, type RouteLocationNormalized, useRouter } from 'vue-router';

export type TUsePrevRouteOptions = {
  /** 前のルートが存在しない場合のフォールバック先のルート名 */
  fallbackRouteName?: string;
  /** フォールバック時のクエリパラメータを生成する関数 */
  fallbackQuery?: () => LocationQueryRaw;
};

const usePrevRoute = (options: TUsePrevRouteOptions = {}) => {
  const router = useRouter();
  const prevRoute: Ref<RouteLocationNormalized | undefined> = ref();

  const setPrevRoute = (route: RouteLocationNormalized) => {
    prevRoute.value = route;
  };

  const canBrowserGoBack = computed(() => {
    // ブラウザの履歴エントリが2つ以上ある場合（現在のページ + 戻る先）
    // 注意: 完璧な判定ではないが、実用的な近似値
    return window.history.length > 1;
  });

  const canGoBack = computed(() => {
    if (prevRoute.value?.name || options.fallbackRouteName) {
      return true;
    } else {
      return canBrowserGoBack.value;
    }
  });

  const goBack = async () => {
    if (!canGoBack.value) return;

    if (prevRoute.value?.name) {
      router.back();
    } else if (options.fallbackRouteName) {
      const query = options.fallbackQuery ? options.fallbackQuery() : undefined;
      await router.push({ 
        name: options.fallbackRouteName, 
        query,
      });
    } else {
      router.back();
    }
  };
  
  return {
    setPrevRoute,
    goBack,
    canGoBack,
    canBrowserGoBack,
  };
};

export {
  usePrevRoute,
};
